import { LANGUAGE_FR } from 'shared/consts/languages'
import {
  TENANT_AT,
  TENANT_CH,
  TENANT_COM,
  TENANT_FR,
  TENANT_IT,
} from 'shared/consts/tenants'
import {
  EXPERIMENT_VARIATION,
  SERP_PRE_QUERY_SUGGESTIONS_PER_TENANT_EXPERIMENT,
} from 'shared/experiments/consts'
import { getExperiment } from 'shared/experiments/utils/experiments'
import { useGetExperimentNamePerTenant } from 'shared/experiments/utils/tenantBasedExperimentsUtils'
import type { Experiment, Tenant } from 'types/shopConfig'

export const usePreQuerySuggestionsExperimentEnabledAndNotDefault = (
  experiments: Experiment[]
) => {
  const serpPreQuerySuggestionsPerTenantExperiment =
    useGetExperimentNamePerTenant(
      SERP_PRE_QUERY_SUGGESTIONS_PER_TENANT_EXPERIMENT
    )
  const { isEnabled, variant } = getExperiment(
    serpPreQuerySuggestionsPerTenantExperiment,
    experiments
  )
  return isEnabled && variant !== EXPERIMENT_VARIATION.DEFAULT
}

export const getPopularCategoriesPerTenant = (
  tenant: Tenant,
  language: string
) => {
  switch (tenant) {
    case TENANT_COM:
      return [
        {
          name: 'Vitamine & Mineralstoffe',
          url: '/vitamine-mineralstoffe-enzyme/',
        },
        {
          name: 'Magen & Darm',
          url: '/magen-darm/',
        },
        {
          name: 'Schmerzen',
          url: '/schmerzmittel/',
        },
      ]
    case TENANT_AT:
      return [
        {
          name: 'Vitamine & Mineralstoffe',
          url: '/vitamine-mineralstoffe-enzyme/',
        },
        {
          name: 'Erkältung & Grippe',
          url: '/erkaeltung/',
        },
        {
          name: 'Magen & Darm',
          url: '/magen-darm/',
        },
      ]
    case TENANT_IT:
      return [
        {
          name: 'Stomaco e Intestino',
          url: '/stomaco-intestino/',
        },
        {
          name: 'Viso',
          url: '/viso/',
        },
        {
          name: 'Alimentazione',
          url: '/alimentazione-dieta/',
        },
      ]
    case TENANT_FR:
      return [
        {
          name: 'Compléments santé',
          url: '/complements-sante/',
        },
        {
          name: 'Alimentation animale',
          url: '/nourriture-animaux/',
        },
        {
          name: 'Visage',
          url: '/visage/',
        },
      ]
    case TENANT_CH:
      return language === LANGUAGE_FR
        ? [
            {
              name: 'Visage',
              url: '/fr/visage/',
            },
            {
              name: 'Vitamines & minéraux',
              url: '/fr/vitamines-mineraux/',
            },
            {
              name: 'Corps',
              url: '/fr/corps/',
            },
          ]
        : [
            {
              name: 'Gesicht',
              url: '/de/gesicht/',
            },
            {
              name: 'Vitamine & Mineralstoffe',
              url: '/de/vitamine-mineralstoffe/',
            },
            {
              name: 'Körper',
              url: '/de/koerper/',
            },
          ]
    default:
      return null
  }
}

export const getPopularBrandsPerTenant = (tenant: Tenant, language: string) => {
  switch (tenant) {
    case TENANT_COM:
      return [
        {
          name: 'CeraVe',
          url: '/markenshops/ms-cerave/',
        },
        {
          name: 'La Roche Posay',
          url: '/markenshops/roche-posay-produktlinien/',
        },
        {
          name: 'Bepanthen',
          url: '/marken/bepanthen/',
        },
      ]
    case TENANT_AT:
      return [
        {
          name: 'CeraVe',
          url: '/cerave-beauty/',
        },
        {
          name: 'Bepanthen',
          url: '/marken/bepanthen/',
        },
        {
          name: 'La Roche Posay',
          url: '/la-roche-posay-beauty/all-from-category/',
        },
      ]
    case TENANT_IT:
      return [
        {
          name: 'CeraVe',
          url: '/marchi/cerave/',
        },
        {
          name: 'La Roche-Posay',
          url: '/marchi/la-roche-posay/',
        },
        {
          name: 'Avéne',
          url: '/marchi/avene/',
        },
      ]
    case TENANT_FR:
      return [
        {
          name: 'CeraVe',
          url: '/marques/cerave-marque/',
        },
        {
          name: 'Avéne',
          url: '/marques/avene/',
        },
        {
          name: 'La Roche-Posay',
          url: '/espace-marques/la-roche-posay/',
        },
      ]
    case TENANT_CH:
      return language === LANGUAGE_FR
        ? [
            {
              name: 'La Roche Posay',
              url: '/fr/marques/la-roche-posay/',
            },
            {
              name: 'Eucerin',
              url: '/fr/marques/eucerin/',
            },
            {
              name: 'CeraVe',
              url: '/fr/marques/cerave/',
            },
          ]
        : [
            {
              name: 'La Roche Posay',
              url: '/de/marken/la-roche-posay/',
            },
            {
              name: 'Eucerin',
              url: '/de/marken/eucerin/',
            },
            {
              name: 'CeraVe',
              url: '/de/marken/cerave/',
            },
          ]
    default:
      return null
  }
}

export const getTranslationsPerTenant = (tenant: Tenant, language: string) => {
  switch (tenant) {
    case TENANT_FR:
      return {
        categories: 'Catégories populaires',
        brands: 'Marques populaires',
      }
    case TENANT_IT:
      return {
        categories: 'Categorie popolari',
        brands: 'Marchi popolari',
      }
    case TENANT_CH:
      return language === LANGUAGE_FR
        ? {
            categories: 'Catégories populaires',
            brands: 'Marques populaires',
          }
        : {
            categories: 'Beliebte Kategorien',
            brands: 'Beliebte Marken',
          }
    default:
      return {
        categories: 'Beliebte Kategorien',
        brands: 'Beliebte Marken',
      }
  }
}
